"use client";

import { useCallback, useEffect, useState } from "react";
import posthog from "posthog-js";
import { useDropzone } from "react-dropzone";
import { AlertTriangle, Loader2, ShieldCheck, UploadCloud } from "lucide-react";
import { repairFitClient, type RepairResult } from "@/lib/client-convert";
import { triggerDownload } from "@/lib/download";
import { RouteMap } from "@/components/route-map";
import { AnimatedStatCard } from "@/components/animated-stat-card";
import { DownloadConfirmation } from "@/components/download-confirmation";
import { SignInGateModal } from "@/components/sign-in-gate-modal";
import { useAnonGate } from "@/hooks/use-anon-gate";
import { type UnitSystem, formatDistance, formatDuration, getDefaultUnitSystem } from "@/lib/units";

type Status = "idle" | "processing" | "done" | "error";

// A single-purpose "drop a FIT file, get a repaired one back" tool — no
// format selection, no trim/strip/HR-fix editors. Output is always FIT.
export function RepairConverter() {
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RepairResult | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [units, setUnits] = useState<UnitSystem>("metric");
  const { showSignInGate, setShowSignInGate, checkAnonGate, recordAnonConversion } = useAnonGate();

  useEffect(() => {
    setUnits(getDefaultUnitSystem());
  }, []);

  const getDownloadName = useCallback((f: File) => {
    const base = f.name.replace(/\.fit$/i, "") || "workout";
    return `${base}-repaired.fit`;
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setStatus("idle");
    setError(null);
    setResult(null);
    setDownloadUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
  }, []);

  const handleFileSelected = useCallback(
    async (f: File) => {
      if (checkAnonGate()) return;

      setFile(f);
      setError(null);
      setResult(null);
      setDownloadUrl(null);
      setStatus("processing");

      try {
        // A single decode produces both the summary and the repaired blob —
        // avoids parsing the file twice.
        const repaired = await repairFitClient(f);
        const url = URL.createObjectURL(repaired.blob);

        setResult(repaired);
        setDownloadUrl(url);
        setStatus("done");

        posthog.capture("fit_file_repaired", {
          corrupted: !!repaired.summary.corrupted,
          recovered_points: repaired.summary.points,
        });

        triggerDownload(url, getDownloadName(f));
        recordAnonConversion();
      } catch (err) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    },
    [checkAnonGate, getDownloadName, recordAnonConversion]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const f = acceptedFiles?.[0];
      if (f) handleFileSelected(f);
    },
    disabled: status === "processing",
    multiple: false,
    accept: { "application/octet-stream": [".fit"] },
    noClick: true,
    noKeyboard: true,
  });

  if (status === "done" && downloadUrl && file && result) {
    const { summary, points } = result;
    const positions: [number, number][] = summary.hasGps
      ? (points
          .filter((p) => p.lat !== undefined && p.lon !== undefined)
          .map((p) => [p.lat as number, p.lon as number]) as [number, number][])
      : [];
    const hasMap = positions.length > 0;

    return (
      <div className="w-full max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DownloadConfirmation
            title={summary.corrupted ? "Your FIT file has been repaired" : "Your file is already valid"}
            fileName={getDownloadName(file)}
            onDownloadAgain={() => triggerDownload(downloadUrl, getDownloadName(file))}
            onReset={reset}
            resetLabel="Repair another file"
            className="h-full flex flex-col justify-center px-6 py-12 max-w-3xl mx-auto w-full"
            boxClassName="h-full rounded-xl p-8 glass-panel flex flex-col items-center justify-center text-center gap-6"
          />

          <div className="h-full rounded-xl p-6 glass-panel">
            {summary.corrupted ? (
              <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 mb-6">
                <AlertTriangle className="size-5 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm">
                  <span className="font-bold">Corruption detected and repaired.</span> We recovered{" "}
                  {summary.points.toLocaleString()} trackpoints before the file became unreadable. Your
                  download contains everything we could recover.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/10 p-4 mb-6">
                <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm">
                  <span className="font-bold">No corruption detected.</span> Your file was already
                  valid — here&apos;s a freshly re-encoded copy.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <AnimatedStatCard label="Trackpoints" value={summary.points.toLocaleString()} />
              {summary.durationMs > 0 && (
                <AnimatedStatCard label="Duration" value={formatDuration(summary.durationMs)} />
              )}
              {summary.distanceM > 0 && (
                <AnimatedStatCard label="Distance" value={formatDistance(summary.distanceM, units)} />
              )}
            </div>

            {hasMap && (
              <div className="relative h-[300px] rounded-xl overflow-hidden border border-white/5">
                <RouteMap positions={positions} />
              </div>
            )}
          </div>
        </div>
        <SignInGateModal open={showSignInGate} onOpenChange={setShowSignInGate} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto px-6">
      <div
        {...getRootProps()}
        className="bg-slate-900/40 rounded-2xl border border-white/5 shadow-2xl p-8 backdrop-blur-sm"
      >
        <input {...getInputProps()} aria-label="Select a FIT file to repair" />
        <div
          className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center bg-white/5 group cursor-pointer transition-colors ${
            isDragActive ? "border-primary/60 bg-primary/5" : "border-white/10 hover:border-primary/40"
          }`}
          onClick={() => { if (!checkAnonGate()) open(); }}
        >
          <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
            {status === "processing" ? (
              <Loader2 className="size-8 animate-spin" />
            ) : (
              <UploadCloud className="size-8" />
            )}
          </div>
          <h3 className="text-lg font-bold mb-1">
            {status === "processing" ? "Repairing your file..." : "Drop your FIT file here"}
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            We&apos;ll recover everything readable and hand back a clean FIT file.
          </p>
          <button
            type="button"
            disabled={status === "processing"}
            className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all disabled:opacity-50"
            onClick={(e) => { e.stopPropagation(); if (!checkAnonGate()) open(); }}
          >
            Select FIT File
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-destructive mt-4 text-center">{error}</p>}
      <SignInGateModal open={showSignInGate} onOpenChange={setShowSignInGate} />
    </div>
  );
}
